import React, { useState, useEffect, useRef } from 'react';

/**
 * The podium shared by "מה חדש" and the rankings page (§12.10, §12.11):
 * the leaders as posters, places 1-3 in Olympic medal colours. It shows the
 * headline only — a name, one number, an optional chip line — and the table
 * under it always starts at #1 and carries the rest of the figures.
 *
 * `max` > 3 lets a wide card show more places (the rankings show 5 from
 * `wideAt` pixels of card width); a phone and a narrow card always show 3.
 */

export const MEDALS = [
  { c: '#d4a017', tint: 'rgba(212,160,23,0.18)' },
  { c: '#9ca3af', tint: 'rgba(156,163,175,0.22)' },
  { c: '#cd7f32', tint: 'rgba(205,127,50,0.18)' },
];
export const medalColor = (i) => (i < 3 ? MEDALS[i].c : 'var(--faint)');

// Occupancy colours: under 10% red, under 18% amber, 18% and up green.
export const occColor = (p) => (p < 10 ? 'var(--danger)' : p < 18 ? 'var(--warn)' : 'var(--ok)');

// The width of a card, so its rows can carry as many columns as fit.
export function useBoxWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// A row's place number, 1-3 in medal colours.
export function RankNum({ i }) {
  return (
    <div style={{ width: 22, flexShrink: 0, textAlign: 'center', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                  color: medalColor(i) }}>{i + 1}</div>
  );
}

const fmt = (v) => (v || 0).toLocaleString();

export default function Podium({ items, mob, onClick, itemKey, poster, name, value, extra, max = 3, wideAt = 1000 }) {
  const [ref, w] = useBoxWidth();
  const n = !mob && max > 3 && w >= wideAt ? max : 3;
  const list = items.slice(0, n);
  const five = n > 3;
  return (
    <div ref={ref} style={{ display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gap: mob ? 8 : 12, marginBottom: 10 }}>
      {list.map((it, i) => {
        const tint = i < 3 ? MEDALS[i].tint : 'transparent';
        const chips = extra && extra(it);
        return (
          <div key={itemKey ? itemKey(it) : i} data-podium-place={i + 1} onClick={onClick ? () => onClick(it) : undefined} style={{
            cursor: onClick ? 'pointer' : 'default', borderRadius: 14, padding: mob ? 8 : 12, minWidth: 0,
            background: `linear-gradient(160deg, ${tint}, var(--surf-alt) 70%)`,
            display: 'flex', flexDirection: mob ? 'column' : 'row', alignItems: 'center', gap: mob ? 6 : five ? 12 : 14,
          }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {poster(it)}
              <div data-medal style={{ position: 'absolute', top: -6, right: -6, width: 24, height: 24, borderRadius: 12,
                            background: medalColor(i), color: '#fff', fontSize: 12, fontWeight: 800,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
            </div>
            <div style={{ minWidth: 0, width: '100%', textAlign: mob ? 'center' : 'right' }}>
              <div data-podium-name style={{ fontSize: mob ? 12 : five ? 14 : 15, fontWeight: 800, color: 'var(--txt)', lineHeight: 1.3,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {name(it)}
              </div>
              <div style={{ fontSize: mob ? 17 : five ? 22 : 26, fontWeight: 900, color: 'var(--accent)', marginTop: mob ? 2 : 6, letterSpacing: -0.5 }}>
                {fmt(value(it))}
              </div>
              {chips && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: mob ? 'center' : 'flex-start', flexWrap: 'wrap', marginTop: 4 }}>
                  {chips}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
